import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Synchronizes the active ingestion document selection with a URL search param.
 * Keeps a single source of truth so both the UI and sharable links stay aligned.
 */
export interface UseComplianceFolderSearchParamOptions<TValue> {
  parse?: (value: string | null) => TValue;
  serialize?: (value: TValue) => string | null;
  isEqual?: (a: TValue, b: TValue) => boolean;
  historyMode?: 'replace' | 'push';
}

export const useComplianceFolderSearchParam = <TValue = string | null>(
  paramName = 'document',
  options?: UseComplianceFolderSearchParamOptions<TValue>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsString = searchParams.toString();

  const parse = useMemo(() => {
    const defaultParse = (value: string | null): string | null => {
      if (!value) {
        return null;
      }

      const trimmedValue = value.trim();

      return trimmedValue.length > 0 ? trimmedValue : null;
    };

    return (
      options?.parse ||
      (defaultParse as unknown as (value: string | null) => TValue)
    );
  }, [options?.parse]);

  const serialize = useMemo(() => {
    const defaultSerialize = (value: string | null): string | null => {
      if (!value) {
        return null;
      }

      const trimmedValue = value.trim();

      return trimmedValue.length > 0 ? trimmedValue : null;
    };

    return (
      options?.serialize ||
      (defaultSerialize as unknown as (value: TValue) => string | null)
    );
  }, [options?.serialize]);

  const isEqual = useMemo(
    () => options?.isEqual || Object.is,
    [options?.isEqual]
  );

  const selectedValue = useMemo(() => {
    const params = new URLSearchParams(searchParamsString);
    const value = params.get(paramName);

    return parse(value);
  }, [paramName, parse, searchParamsString]);

  const setSelectedValue = useCallback(
    (nextValue: TValue) => {
      if (isEqual(nextValue, selectedValue)) {
        return;
      }

      const serializedValue = serialize(nextValue);

      const nextSearchParams = new URLSearchParams(searchParamsString);

      if (serializedValue) {
        nextSearchParams.set(paramName, serializedValue);
      } else {
        nextSearchParams.delete(paramName);
      }

      setSearchParams(nextSearchParams, {
        replace: options?.historyMode !== 'push',
      });
    },
    [
      isEqual,
      options?.historyMode,
      paramName,
      searchParamsString,
      selectedValue,
      serialize,
      setSearchParams,
    ]
  );

  return [selectedValue, setSelectedValue] as const;
};
